import { NavLink } from "react-router-dom";
import BrandLogo from "@/components/feature/BrandLogo";
import { adminNav, type AdminNavItem } from "@/pages/admin/navConfig";

interface AdminSidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  pendingBookings: number;
  pendingReviews: number;
  onCloseMobile: () => void;
  onToggleCollapse: () => void;
  onSignOut: () => void;
}

export default function AdminSidebar({
  collapsed,
  mobileOpen,
  pendingBookings,
  pendingReviews,
  onCloseMobile,
  onToggleCollapse,
  onSignOut,
}: AdminSidebarProps) {
  const badgeFor = (item: AdminNavItem) => {
    if (item.badgeKey === "pendingBookings") return pendingBookings;
    if (item.badgeKey === "pendingReviews") return pendingReviews;
    return item.badge || 0;
  };

  return (
    <>
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-40 bg-foreground-950/50 lg:hidden"
          onClick={onCloseMobile}
          aria-hidden="true"
        ></div>
      ) : null}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col bg-primary-950 text-background-100 transition-[transform,width] duration-300 w-[272px] ${
          collapsed ? "lg:w-[78px]" : "lg:w-[272px]"
        } ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        <div className="flex items-center gap-2 px-3.5 h-16 border-b border-primary-800 shrink-0">
          <div className={collapsed ? "lg:hidden" : ""}>
            <BrandLogo imgClassName="h-9" />
          </div>
          {collapsed ? (
            <span className="hidden lg:flex w-11 h-11 items-center justify-center rounded-lg bg-background-50 text-primary-800 mx-auto">
              <i className="ri-plant-fill text-xl"></i>
            </span>
          ) : null}

          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="ml-auto hidden lg:flex w-8 h-8 items-center justify-center rounded-md text-background-300 hover:bg-primary-800 hover:text-background-50 transition-colors cursor-pointer shrink-0"
          >
            <i className={collapsed ? "ri-menu-unfold-line" : "ri-menu-fold-line"}></i>
          </button>
          <button
            type="button"
            onClick={onCloseMobile}
            aria-label="Close menu"
            className="ml-auto lg:hidden w-9 h-9 flex items-center justify-center rounded-md text-background-200 hover:bg-primary-800 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {!collapsed ? (
          <div className="hidden lg:block px-4 pt-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-accent-300">
              Agri Tourism Admin
            </p>
          </div>
        ) : null}

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {adminNav.map((group) => (
            <div key={group.heading} className={collapsed ? "lg:mb-1" : "mb-1"}>
              <p
                className={`text-[10px] font-semibold uppercase tracking-[0.18em] text-background-400 px-3 mt-4 mb-1.5 ${
                  collapsed ? "lg:hidden" : ""
                }`}
              >
                {group.heading}
              </p>
              {collapsed ? (
                <span className="hidden lg:block h-px bg-primary-800 mx-3 my-2"></span>
              ) : null}

              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const badge = badgeFor(item);
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === "/admin"}
                        title={collapsed ? item.label : undefined}
                        className={({ isActive }) =>
                          `group relative flex items-center gap-3 rounded-md text-sm font-medium transition-colors ${
                            collapsed ? "lg:justify-center lg:px-2" : ""
                          } px-3 py-2.5 ${
                            isActive
                              ? "bg-primary-800 text-background-50"
                              : "text-background-200 hover:bg-primary-800/60 hover:text-background-50"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            {isActive ? (
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r bg-accent-500"></span>
                            ) : null}
                            <span className="w-5 h-5 flex items-center justify-center shrink-0">
                              <i className={`${item.icon} text-lg`}></i>
                            </span>
                            <span className={collapsed ? "lg:hidden truncate" : "truncate"}>
                              {item.label}
                            </span>
                            {badge > 0 ? (
                              collapsed ? (
                                <span className="hidden lg:block absolute top-1.5 right-2 w-2 h-2 rounded-full bg-accent-500"></span>
                              ) : (
                                <span className="ml-auto text-[10px] font-bold bg-accent-500 text-accent-950 rounded-full px-2 py-0.5 min-w-[20px] text-center">
                                  {badge}
                                </span>
                              )
                            ) : null}
                          </>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-primary-800 p-3 shrink-0">
          <NavLink
            to="/admin/help"
            title={collapsed ? "Help & Support" : undefined}
            className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-background-200 hover:bg-primary-800/60 hover:text-background-50 transition-colors ${
              collapsed ? "lg:justify-center lg:px-2" : ""
            }`}
          >
            <span className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-lifebuoy-line text-lg"></i>
            </span>
            <span className={collapsed ? "lg:hidden" : ""}>Help &amp; Support</span>
          </NavLink>

          <div
            className={`mt-2 flex items-center gap-3 rounded-lg bg-primary-900/70 border border-primary-800 px-3 py-2.5 ${
              collapsed ? "lg:justify-center lg:px-2" : ""
            }`}
          >
            <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500 text-accent-950 text-xs font-bold shrink-0">
              SD
            </span>
            <div className={`min-w-0 ${collapsed ? "lg:hidden" : ""}`}>
              <p className="text-sm font-semibold text-background-50 truncate">Suresh DNV</p>
              <p className="text-[11px] text-background-300 truncate">Super Admin</p>
            </div>
            <button
              type="button"
              onClick={onSignOut}
              aria-label="Sign out"
              title="Sign out"
              className={`ml-auto w-8 h-8 items-center justify-center rounded-md text-background-300 hover:bg-primary-800 hover:text-background-50 transition-colors cursor-pointer shrink-0 ${
                collapsed ? "lg:hidden flex" : "flex"
              }`}
            >
              <i className="ri-logout-box-r-line"></i>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}